/**
 * Cascade FOREST builder for the capability-map overlay.
 *
 * P85.2 shipped this as a single-origin tree: distances were
 * measured from ONE incident origin, so only cells downstream of
 * that origin could draw a cause edge. When a cascade has several
 * INDEPENDENT upstream causes (e.g. a card-rail incident AND a
 * messaging incident rooting separate chains that converge on
 * "Customer payments"), the second chain's cells lit but drew no
 * connecting line (P89 close-out follow-up).
 *
 * P90 generalises it to a forest: every most-upstream hot cell is
 * a root, and every other hot cell connects to its closest hot
 * upstream cause -- so all independent chains render. Still calm:
 * exactly one parent per non-root hot cell (no spaghetti).
 */
import type { CapabilityCell, CapabilityDependencyEdge } from "shared/api/types";
import type { CapabilityState } from "twin/capmap/stateMap";

export interface TreeEdge {
  upstreamId: string;
  dependentId: string;
}

const HOT_STATES = new Set<CapabilityState>(["down", "degraded", "at-risk"]);

/** Multi-source BFS depth over a hot-only DEPENDENTS adjacency:
 * every root is depth 0, its hot dependents 1, etc. Used to order
 * "closest cause" parent selection. Cells unreachable from any
 * root (cycles) are absent from the map. */
export function depthsFromRoots(
  roots: string[],
  hotDependents: Map<string, string[]>,
): Map<string, number> {
  const depth = new Map<string, number>();
  const queue: string[] = [];
  for (const r of roots) {
    depth.set(r, 0);
    queue.push(r);
  }
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const d = depth.get(cur)!;
    for (const next of hotDependents.get(cur) ?? []) {
      if (!depth.has(next)) {
        depth.set(next, d + 1);
        queue.push(next);
      }
    }
  }
  return depth;
}

/** Build the calm cascade forest over the hot (down/degraded/
 * at-risk) cells:
 *
 *   - hot-only dependency subgraph (drop edges touching a stable
 *     cell -- the cause chain must be hot end-to-end);
 *   - roots = hot cells with no hot upstream (independent causes);
 *   - each non-root hot cell connects to its closest hot upstream
 *     (smallest depth from a root, tie-break smallest id).
 *
 * Returns at most one (upstream -> dependent) edge per non-root hot
 * cell, so the overlay stays a forest of cause chains, never a
 * spaghetti of every dependency edge. */
export function buildCascadeTree(opts: {
  cells: CapabilityCell[];
  edges: CapabilityDependencyEdge[];
  states: Map<string, CapabilityState>;
}): TreeEdge[] {
  const { cells, edges, states } = opts;
  const isHot = (id: string): boolean =>
    HOT_STATES.has(states.get(id) as CapabilityState);
  const hotSet = new Set(cells.filter((c) => isHot(c.id)).map((c) => c.id));
  if (hotSet.size === 0) return [];

  // Hot-only adjacency: keep an edge only when BOTH ends are hot.
  const hotUpstreams = new Map<string, string[]>(); // node -> hot upstream deps
  const hotDependents = new Map<string, string[]>(); // node -> hot dependents
  for (const e of edges) {
    if (!hotSet.has(e.dependent_id) || !hotSet.has(e.upstream_id)) continue;
    if (!hotUpstreams.has(e.dependent_id)) hotUpstreams.set(e.dependent_id, []);
    hotUpstreams.get(e.dependent_id)!.push(e.upstream_id);
    if (!hotDependents.has(e.upstream_id)) hotDependents.set(e.upstream_id, []);
    hotDependents.get(e.upstream_id)!.push(e.dependent_id);
  }

  const roots = [...hotSet].filter((id) => (hotUpstreams.get(id) ?? []).length === 0);
  const depth = depthsFromRoots(roots, hotDependents);

  const out: TreeEdge[] = [];
  for (const id of hotSet) {
    const ups = hotUpstreams.get(id) ?? [];
    if (ups.length === 0) continue; // a root -- no cause edge
    const parent = ups.slice().sort((a, b) => {
      const da = depth.get(a) ?? Infinity;
      const db = depth.get(b) ?? Infinity;
      return da - db || (a < b ? -1 : 1);
    })[0];
    if (parent !== undefined) out.push({ upstreamId: parent, dependentId: id });
  }
  return out;
}
